import React, { useEffect, useState } from "react";
import { Player } from "@remotion/player";
import { Captions, Clock3, Film, Ratio, Type, Download } from "lucide-react";
import { getResolution, getEffectiveDuration, cn } from "@/lib/utils";
import { MainComposition } from "@/remotion/Composition";
import { loadGoogleFont } from "@/lib/font-loader";
import type { AppConfig } from "@/lib/types";
import { EmptyState } from "./EmptyState";
import { AudioEngine } from "@/lib/audio-engine";
import { Button } from "../ui/button";
import { useAppStore } from "@/lib/store";

export function LivePreview({ config, onRecord }: { config: AppConfig, onRecord: () => void }) {
  const [audioDuration, setAudioDuration] = useState(0);
  const { isRecording } = useAppStore();

  useEffect(() => {
    loadGoogleFont(config.fontName);
  }, [config.fontName]);

  useEffect(() => {
    const loadAudioDuration = async () => {
      try {
        const music = config.audio.musicUrl ? await AudioEngine.loadAudio(config.audio.musicUrl) : null;
        const vo = config.audio.voiceoverUrl ? await AudioEngine.loadAudio(config.audio.voiceoverUrl) : null;
        const dur = Math.max(music?.duration || 0, vo?.duration || 0);
        setAudioDuration(dur);
      } catch (e) {
        console.error('Failed to load audio for live preview duration:', e);
      }
    };
    loadAudioDuration();
  }, [config.audio.musicUrl, config.audio.voiceoverUrl]);

  const hasScript = Boolean(config.text.trim());
  const [w, h] = getResolution(config.aspectRatio);
  const durationInSeconds = getEffectiveDuration(config);
  const fps = 30;
  const durationInFrames = Math.max(1, Math.round(durationInSeconds * fps));

  const canRecord = hasScript && !isRecording;

  return (
    <section className="preview-desk flex flex-col h-full">
      <div className="preview-header">
        <div className="flex items-center gap-3">
          <div className="preview-mark">
            <Film className="size-4" />
          </div>
          <div>
            <p className="eyebrow">Stage</p>
            <h2 className="text-lg font-semibold text-foreground">Live Frame</h2>
          </div>
        </div>
      </div>

      <div className="preview-frame-shell flex-1 flex flex-col items-center justify-center overflow-hidden p-4 min-h-0">
        {hasScript ? (
          <div className="w-full h-full flex items-center justify-center">
            <Player
              component={MainComposition}
              inputProps={config}
              durationInFrames={durationInFrames}
              fps={fps}
              compositionWidth={w}
              compositionHeight={h}
              style={{
                width: '100%',
                height: '100%',
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
              controls
              loop
            />
          </div>
        ) : (
          <EmptyState aspectRatio={config.aspectRatio} />
        )}
      </div>

      <div className="preview-stat-grid border-b border-white/5 pb-4">
        <div>
          <Ratio className="size-4" />
          <span>{config.aspectRatio}</span>
        </div>
        <div>
          <Clock3 className="size-4" />
          <span>{config.durationStr}</span>
        </div>
        <div>
          <Type className="size-4" />
          <span>{config.fontName || "Inter"}</span>
        </div>
        <div>
          <Captions className="size-4" />
          <span>{hasScript ? `${config.text.trim().split(/\s+/).length} words` : "Draft"}</span>
        </div>
      </div>

      <div className="p-4 bg-background/50 backdrop-blur-xl mt-auto">
        <Button
          className="h-12 w-full gap-2 text-base font-bold shadow-2xl"
          size="lg"
          disabled={!canRecord}
          onClick={onRecord}
        >
          <Download className={cn("size-5", isRecording && "animate-bounce")} />
          {isRecording ? "Rendering Sequence..." : "Export MP4 Video"}
        </Button>
      </div>
    </section>
  );
}
